/**
 * Sprocket's Python runtime: a thin, well-defined wrapper around a live
 * Pyodide instance.
 *
 * Responsibilities:
 *  - Boot a Pyodide interpreter (via an injected loader, so this is testable
 *    in Node and runnable in the browser with no code changes).
 *  - Capture `stdout`/`stderr` by installing batched stream handlers and
 *    fanning them out to any number of subscribers.
 *  - Expose a small, stable `run(code)` API for downstream UI (REPL, tutor).
 *
 * Everything the UI needs flows through {@link createPythonRuntime}; nothing
 * else in the app should import Pyodide directly.
 */
import type {
  LoadPyodideFn,
  OutputListener,
  PyodideLike,
  PythonRuntime,
} from "./types";

export interface CreateRuntimeOptions {
  /**
   * Factory that returns a live Pyodide instance. In the browser this comes
   * from {@link import("./loader").loadPyodideFromCdn}; in tests it is the
   * `loadPyodide` exported by the `pyodide` npm package.
   */
  loadPyodide: LoadPyodideFn;
  /** Passed through to Pyodide; locates the runtime's support files. */
  indexURL?: string;
}

/**
 * Boot Pyodide and wrap it in a {@link PythonRuntime}.
 *
 * The returned runtime has its output streams already wired: any `print(...)`
 * (or write to `sys.stdout`/`sys.stderr`) from Python is delivered to every
 * registered {@link OutputListener} as an {@link OutputChunk}.
 */
export async function createPythonRuntime(
  options: CreateRuntimeOptions,
): Promise<PythonRuntime> {
  const pyodide: PyodideLike = await options.loadPyodide(
    options.indexURL ? { indexURL: options.indexURL } : undefined,
  );

  // Fan-out set of subscribers. Using a Set keeps unsubscribe O(1) and avoids
  // duplicate registration.
  const listeners = new Set<OutputListener>();

  const emit = (stream: "stdout" | "stderr", text: string): void => {
    for (const listener of listeners) {
      listener({ stream, text });
    }
  };

  // `batched` hands us output a line (or flush) at a time, which is exactly the
  // granularity a REPL/console wants — no manual buffering required.
  pyodide.setStdout({ batched: (text) => emit("stdout", text) });
  pyodide.setStderr({ batched: (text) => emit("stderr", text) });

  // Capture the live interpreter version for the "ready" UI state.
  const pythonVersion = String(
    await pyodide.runPythonAsync(
      "import sys; '.'.join(map(str, sys.version_info[:3]))",
    ),
  );

  return {
    pythonVersion,
    run(code: string): Promise<unknown> {
      return pyodide.runPythonAsync(code);
    },
    onOutput(listener: OutputListener): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
